<template>
    <div class="opc-pdf-sheet" :style="sheetStyle">
        <div class="opc-pdf-sheet__title">{{ sheet.title }}</div>

        <table class="opc-pdf-sheet__info">
            <tbody>
            <tr>
                <th>{{ $t('label.sith.tpm.opcPointCurve.pdfDevice') }}</th>
                <td colspan="3">
                    <span v-if="filled(sheet.device)">{{ sheet.device }}</span>
                    <span v-else class="opc-pdf-sheet__blank"></span>
                </td>
            </tr>
            <tr>
                <th>{{ $t('label.sith.tpm.opcPointCurve.projectCode') }}</th>
                <td>
                    <span v-if="filled(sheet.projectCode)">{{ sheet.projectCode }}</span>
                    <span v-else class="opc-pdf-sheet__blank"></span>
                </td>
                <th>{{ $t('label.sith.tpm.opcPointCurve.orderCode') }}</th>
                <td>
                    <span v-if="filled(sheet.orderCode)">{{ sheet.orderCode }}</span>
                    <span v-else class="opc-pdf-sheet__blank"></span>
                </td>
            </tr>
            <tr>
                <th>{{ $t('label.sith.tpm.opcPointCurve.operationSeq') }}</th>
                <td>
                    <span v-if="filled(sheet.operationSeq)">{{ sheet.operationSeq }}</span>
                    <span v-else class="opc-pdf-sheet__blank"></span>
                </td>
                <th>{{ $t('label.sith.tpm.opcPointCurve.materialCode') }}</th>
                <td>
                    <!-- 物料号选填：未填时留一条空白横线供手写 -->
                    <span v-if="filled(sheet.materialCode)">{{ sheet.materialCode }}</span>
                    <span v-else class="opc-pdf-sheet__blank"></span>
                </td>
            </tr>
            <tr>
                <th>{{ $t('label.sith.tpm.opcPointCurve.timeRange') }}</th>
                <td colspan="3">{{ sheet.timeRange }}</td>
            </tr>
            <tr>
                <th>{{ $t('label.sith.tpm.opcPointCurve.operator') }}</th>
                <td>
                    <span v-if="filled(sheet.operator)">{{ sheet.operator }}</span>
                    <span v-else class="opc-pdf-sheet__blank"></span>
                </td>
                <th>{{ $t('label.sith.tpm.opcPointCurve.exportTime') }}</th>
                <td>{{ sheet.exportTime }}</td>
            </tr>
            </tbody>
        </table>

        <div class="opc-pdf-sheet__chart">
            <img v-if="sheet.chartImage" :src="sheet.chartImage.dataUrl" :style="chartStyle" alt="">
        </div>

        <div class="opc-pdf-sheet__footer">
            <div class="opc-pdf-sheet__sign">
                <span class="opc-pdf-sheet__footer-label">{{ $t('label.sith.tpm.opcPointCurve.signature') }}：</span>
                <span class="opc-pdf-sheet__sign-box"></span>
            </div>
            <div class="opc-pdf-sheet__date">
                <span class="opc-pdf-sheet__footer-label">{{ $t('label.sith.tpm.opcPointCurve.signDate') }}：</span>
                <span class="opc-pdf-sheet__blank opc-pdf-sheet__blank--year"></span>{{ $t('label.sith.tpm.opcPointCurve.year') }}
                <span class="opc-pdf-sheet__blank opc-pdf-sheet__blank--short"></span>{{ $t('label.sith.tpm.opcPointCurve.month') }}
                <span class="opc-pdf-sheet__blank opc-pdf-sheet__blank--short"></span>{{ $t('label.sith.tpm.opcPointCurve.day') }}
            </div>
        </div>
    </div>
</template>

<script>
import {
    SHEET_CHART_HEIGHT_PX,
    SHEET_HEIGHT_PX,
    SHEET_MARGIN_X_PX,
    SHEET_WIDTH_PX,
    textOrEmpty
} from "./opcCurvePdf";

/**
 * OPC 点位曲线导出 PDF 的 A4 竖版版面（验收 #10），只用于离屏渲染后 html2canvas 截图，不在页面上显示。
 * 自上而下：标题；信息表（设备、项目号、工单号、工序号、物料号、时间范围、导出操作人、导出时间，空值画手写下划线）；
 * 曲线图（OpcCurveChart.getViewImage 的当前视图，含图例与纵轴单位）；签字框与「日期：____年__月__日」。
 *
 * props.sheet = { title, device, projectCode, orderCode, operationSeq, materialCode, timeRange, operator, exportTime,
 *                 chartImage: {dataUrl, width, height} | null }
 */
export default {
    name: "OpcCurvePdfSheet",
    props: {
        sheet: {
            type: Object,
            required: true
        }
    },
    computed: {
        sheetStyle() {
            return {
                width: SHEET_WIDTH_PX + 'px',
                height: SHEET_HEIGHT_PX + 'px',
                paddingLeft: SHEET_MARGIN_X_PX + 'px',
                paddingRight: SHEET_MARGIN_X_PX + 'px'
            };
        },
        chartStyle() {
            return {
                width: (SHEET_WIDTH_PX - 2 * SHEET_MARGIN_X_PX) + 'px',
                height: SHEET_CHART_HEIGHT_PX + 'px'
            };
        }
    },
    methods: {
        filled(value) {
            return textOrEmpty(value) !== '';
        }
    }
};
</script>

<style scoped>
.opc-pdf-sheet {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    padding-top: 44px;
    padding-bottom: 48px;
    background: #fff;
    color: #212529;
    font-family: "PingFang SC", "Microsoft YaHei", "Noto Sans CJK SC", "Source Han Sans SC", sans-serif;
    font-size: 13px;
    line-height: 1.5;
}

.opc-pdf-sheet__title {
    margin-bottom: 18px;
    font-size: 22px;
    font-weight: bold;
    text-align: center;
    word-break: break-all;
}

.opc-pdf-sheet__info {
    width: 100%;
    border-collapse: collapse;
}

.opc-pdf-sheet__info th,
.opc-pdf-sheet__info td {
    padding: 6px 10px;
    border: 1px solid #adb5bd;
    vertical-align: bottom;
}

.opc-pdf-sheet__info th {
    background: #f1f3f5;
    font-weight: normal;
    white-space: nowrap;
}

.opc-pdf-sheet__info td {
    word-break: break-all;
}

/* 手写用空白横线 */
.opc-pdf-sheet__blank {
    display: inline-block;
    min-width: 160px;
    height: 1.2em;
    border-bottom: 1px solid #343a40;
    vertical-align: bottom;
}

.opc-pdf-sheet__blank--year {
    min-width: 64px;
}

.opc-pdf-sheet__blank--short {
    min-width: 40px;
}

.opc-pdf-sheet__chart {
    margin-top: 16px;
}

.opc-pdf-sheet__chart img {
    display: block;
    box-sizing: border-box;
    border: 1px solid #dee2e6;
}

.opc-pdf-sheet__footer {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: auto;
    font-size: 14px;
}

.opc-pdf-sheet__sign {
    display: flex;
    align-items: flex-start;
}

.opc-pdf-sheet__footer-label {
    white-space: nowrap;
}

.opc-pdf-sheet__sign-box {
    display: inline-block;
    width: 260px;
    height: 80px;
    border: 1px solid #343a40;
}

.opc-pdf-sheet__date {
    white-space: nowrap;
}
</style>
